// #tag: ADMIN DASHBOARD METRICS SCREEN
// #purpose: Executive control center rendering real-time system health, database status, candidate KPIs, activity trends, and quick administration shortcuts.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Clock3,
  Database,
  DollarSign,
  Plus,
  Server,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { fetchAdminDashboard } from '../../api/admin';
import { Illustration } from '../../components/common/Illustration';
import { LoadingState, ErrorState } from '../../components/states/StateViews';
import {
  ADMIN_PANEL_ILLUSTRATIONS,
  adminKpiIllustration,
  adminModuleIllustration,
} from '../../config/adminIllustrations';
import type { AdminDashboardResponse, AdminKpi } from '../../types/admin';

function KpiCard({ kpi }: { kpi: AdminKpi }) {
  return (
    <article className="wfx-admin-kpi glass-surface has-illustration">
      <Illustration
        context={adminKpiIllustration(kpi.id)}
        size="xs"
        className="wfx-admin-kpi-illustration"
      />
      <p className="wfx-admin-kpi-label">{kpi.label}</p>
      <strong className="wfx-admin-kpi-value">{kpi.value}</strong>
      <span className={`wfx-admin-kpi-delta trend-${kpi.trend}`}>{kpi.delta}</span>
    </article>
  );
}

function PanelIllustration({ context }: { context: string }) {
  return (
    <Illustration context={context} size="sm" className="wfx-admin-panel-illustration" />
  );
}

function MiniBars({ items, max }: { items: Array<{ label: string; value: number }>; max?: number }) {
  const peak = max ?? Math.max(...items.map((i) => i.value), 1);
  return (
    <div className="wfx-admin-mini-bars">
      {items.map((item) => (
        <div key={item.label} className="wfx-admin-mini-bar-row">
          <span>{item.label}</span>
          <div className="wfx-admin-mini-bar-track">
            <span style={{ width: `${(item.value / peak) * 100}%` }} />
          </div>
          <strong>{item.value}%</strong>
        </div>
      ))}
    </div>
  );
}

function DashboardBody({ data }: { data: AdminDashboardResponse }) {
  const [aiPrompt, setAiPrompt] = useState('');
  const [fabOpen, setFabOpen] = useState(false);

  const quickActions = useMemo(() => [
    { label: 'Users', desc: 'Manage Accounts', route: '/admin/users' },
    { label: 'Results', desc: 'Publish Results', route: '/admin/results' },
    { label: 'Opportunities', desc: 'Create Opportunity', route: '/admin/opportunities' },
    { label: 'Media', desc: 'Upload Assets', route: '/admin/media' },
    { label: 'Notifications', desc: 'Send Notification', route: '/admin/notifications' },
    { label: 'SEO', desc: 'Manage Metadata', route: '/admin/seo' },
    { label: 'CMS', desc: 'Edit Content', route: '/admin/content/pages' },
    { label: 'Support', desc: 'Resolve Tickets', route: '/admin/support' },
  ], []);

  return (
    <div className="wfx-admin-dashboard">
      {data.role === 'super_admin' && (
        <section className="wfx-admin-hero glass-surface has-illustration">
          <div className="wfx-admin-hero-copy">
            <p className="section-kicker">Super Admin</p>
            <h2>Platform Command Center</h2>
            <p className="wfx-muted">Full operational visibility across users, content, infrastructure, and billing.</p>
            <Link to="/admin/users" className="primary-action compact">Manage Users</Link>
          </div>
          <Illustration context="admin.hero" size="md" className="wfx-admin-hero-illustration" />
        </section>
      )}

      <section className="wfx-admin-kpi-grid">
        {data.kpis.map((kpi) => <KpiCard key={kpi.id} kpi={kpi} />)}
      </section>

      {data.superAdmin && (
        <>
          <section className="wfx-admin-grid-two">
            <article className="wfx-admin-panel glass-surface has-illustration">
              <PanelIllustration context={ADMIN_PANEL_ILLUSTRATIONS.revenue} />
              <header className="wfx-admin-panel-head">
                <h2><DollarSign size={18} /> Revenue & Billing</h2>
              </header>
              <div className="wfx-admin-stat-row">
                <div><strong>{data.superAdmin.revenue.value}</strong><span>{data.superAdmin.revenue.label}</span></div>
                <div><strong>{data.superAdmin.billing.plan}</strong><span>Plan</span></div>
                <div><strong>{data.superAdmin.billing.nextInvoice}</strong><span>Next invoice</span></div>
              </div>
              <p className="wfx-muted">{data.superAdmin.revenue.delta}</p>
            </article>
            <article className="wfx-admin-panel glass-surface has-illustration">
              <PanelIllustration context={ADMIN_PANEL_ILLUSTRATIONS.api} />
              <header className="wfx-admin-panel-head">
                <h2><Server size={18} /> API Usage</h2>
              </header>
              <div className="wfx-admin-stat-row">
                <div><strong>{data.superAdmin.apiUsage.today.toLocaleString()}</strong><span>Requests today</span></div>
                <div><strong>{data.superAdmin.apiUsage.limit.toLocaleString()}</strong><span>Daily limit</span></div>
                <div><strong>{Math.round((data.superAdmin.apiUsage.today / data.superAdmin.apiUsage.limit) * 100)}%</strong><span>Utilization</span></div>
              </div>
              <p className="wfx-muted">{data.superAdmin.apiUsage.delta}</p>
            </article>
          </section>
          <section className="wfx-admin-grid-two">
            <article className="wfx-admin-panel glass-surface has-illustration">
              <PanelIllustration context={ADMIN_PANEL_ILLUSTRATIONS.database} />
              <header className="wfx-admin-panel-head">
                <h2><Database size={18} /> Database Statistics</h2>
              </header>
              <div className="wfx-admin-calendar-grid">
                <div className="wfx-admin-calendar-card"><strong>{data.superAdmin.database.users}</strong><span>Users</span></div>
                <div className="wfx-admin-calendar-card"><strong>{data.superAdmin.database.results}</strong><span>Results</span></div>
                <div className="wfx-admin-calendar-card"><strong>{data.superAdmin.database.opportunities}</strong><span>Opportunities</span></div>
                <div className="wfx-admin-calendar-card"><strong>{data.superAdmin.database.vaultDocs}</strong><span>Vault docs</span></div>
                <div className="wfx-admin-calendar-card"><strong>{data.superAdmin.database.estimatedMb} MB</strong><span>Est. storage</span></div>
              </div>
            </article>
            <article className="wfx-admin-panel glass-surface has-illustration">
              <PanelIllustration context={ADMIN_PANEL_ILLUSTRATIONS.security} />
              <header className="wfx-admin-panel-head">
                <h2><AlertTriangle size={18} /> Security Alerts</h2>
              </header>
              <ul className="wfx-admin-activity-list">
                {data.superAdmin.securityAlerts.map((alert) => (
                  <li key={alert.id}>
                    <span className={`wfx-admin-alert-dot level-${alert.level}`} />
                    <span className="wfx-admin-activity-time">{alert.time}</span>
                    <span>{alert.message}</span>
                  </li>
                ))}
              </ul>
            </article>
          </section>
          <section className="wfx-admin-panel glass-surface has-illustration">
            <PanelIllustration context={ADMIN_PANEL_ILLUSTRATIONS.logs} />
            <header className="wfx-admin-panel-head">
              <h2><Server size={18} /> System Logs</h2>
              <Link to="/admin/audit" className="text-action">View audit logs</Link>
            </header>
            <div className="wfx-admin-table-wrap">
              <table className="wfx-admin-table">
                <thead><tr><th>Level</th><th>Message</th><th>Time</th></tr></thead>
                <tbody>
                  {data.superAdmin.systemLogs.map((log) => (
                    <tr key={log.id}><td>{log.level}</td><td>{log.message}</td><td>{log.time}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      <section className="wfx-admin-grid-two">
        <article className="wfx-admin-panel glass-surface has-illustration">
          <PanelIllustration context={ADMIN_PANEL_ILLUSTRATIONS.analytics} />
          <header className="wfx-admin-panel-head">
            <h2><TrendingUp size={18} /> Visitors</h2>
            <span className="wfx-muted">Today · Yesterday · Last week</span>
          </header>
          <div className="wfx-admin-stat-row">
            <div><strong>{data.analytics.visitors.today}</strong><span>Today</span></div>
            <div><strong>{data.analytics.visitors.yesterday}</strong><span>Yesterday</span></div>
            <div><strong>{data.analytics.visitors.lastWeek}</strong><span>Last week</span></div>
          </div>
        </article>

        <article className="wfx-admin-panel glass-surface has-illustration">
          <PanelIllustration context={ADMIN_PANEL_ILLUSTRATIONS.analytics} />
          <header className="wfx-admin-panel-head">
            <h2><BarChart3 size={18} /> User Registrations</h2>
          </header>
          <div className="wfx-admin-stat-row">
            <div><strong>{data.analytics.registrations.daily}</strong><span>Daily</span></div>
            <div><strong>{data.analytics.registrations.weekly}</strong><span>Weekly</span></div>
            <div><strong>{data.analytics.registrations.monthly}</strong><span>Monthly</span></div>
          </div>
        </article>
      </section>

      <section className="wfx-admin-grid-two">
        <article className="wfx-admin-panel glass-surface">
          <header className="wfx-admin-panel-head"><h2>Traffic Sources</h2></header>
          <MiniBars items={data.analytics.trafficSources} />
        </article>
        <article className="wfx-admin-panel glass-surface">
          <header className="wfx-admin-panel-head"><h2>Top Modules</h2></header>
          <MiniBars items={data.analytics.topModules} />
        </article>
      </section>

      <section className="wfx-admin-panel glass-surface has-illustration">
        <PanelIllustration context={ADMIN_PANEL_ILLUSTRATIONS.health} />
        <header className="wfx-admin-panel-head"><h2>Platform Health</h2></header>
        <div className="wfx-admin-health-grid">
          {data.health.map((item) => (
            <div key={item.id} className={`wfx-admin-health-card status-${item.status}`}>
              <CheckCircle2 size={16} />
              <strong>{item.label}</strong>
              <span>{item.status}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="wfx-admin-grid-two">
        <article className="wfx-admin-panel glass-surface has-illustration">
          <PanelIllustration context={ADMIN_PANEL_ILLUSTRATIONS.approvals} />
          <header className="wfx-admin-panel-head">
            <h2>Pending Approvals</h2>
            <Link to="/admin/content/pages" className="text-action">View all</Link>
          </header>
          <div className="wfx-admin-table-wrap">
            <table className="wfx-admin-table">
              <thead>
                <tr><th>Type</th><th>Title</th><th>Author</th><th>Date</th><th>Action</th></tr>
              </thead>
              <tbody>
                {data.pendingApprovals.map((row) => (
                  <tr key={row.id}>
                    <td>{row.type}</td>
                    <td>{row.title}</td>
                    <td>{row.author}</td>
                    <td>{row.date}</td>
                    <td><button type="button" className="chip-pill">Review</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="wfx-admin-panel glass-surface has-illustration">
          <PanelIllustration context={ADMIN_PANEL_ILLUSTRATIONS.activity} />
          <header className="wfx-admin-panel-head"><h2><Activity size={18} /> Recent Activity</h2></header>
          <ul className="wfx-admin-activity-list">
            {data.recentActivity.map((item) => (
              <li key={`${item.time}-${item.message}`}>
                <Clock3 size={14} />
                <span className="wfx-admin-activity-time">{item.time}</span>
                <span>{item.message}</span>
              </li>
            ))}
          </ul>
        </article>
      </section>

      <section className="wfx-admin-module-grid">
        {quickActions.map((item) => (
          <Link key={item.label} to={item.route} className="wfx-admin-module-card glass-surface has-illustration">
            <Illustration
              context={adminModuleIllustration(item.label)}
              size="xs"
              className="wfx-admin-module-illustration"
            />
            <strong>{item.label}</strong>
            <span>{item.desc}</span>
          </Link>
        ))}
      </section>

      <section className="wfx-admin-grid-two">
        <article className="wfx-admin-panel glass-surface has-illustration">
          <PanelIllustration context={ADMIN_PANEL_ILLUSTRATIONS.ai} />
          <header className="wfx-admin-panel-head"><h2><Sparkles size={18} /> AI Assistant</h2></header>
          <div className="wfx-admin-ai-actions">
            {[
              'Summarize Official Notification',
              'Generate Opportunity Description',
              'Generate SEO Metadata',
              'Generate FAQs',
              'Extract Important Dates',
            ].map((action) => (
              <button key={action} type="button" className="chip-pill">{action}</button>
            ))}
          </div>
          <label className="wfx-admin-ai-prompt">
            <span className="wfx-muted">Ask AI to assist with administration…</span>
            <textarea
              rows={3}
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Ask AI to assist with administration…"
            />
          </label>
        </article>

        <div className="wfx-admin-stack">
          {data.support && (
            <article className="wfx-admin-panel glass-surface has-illustration">
              <PanelIllustration context={ADMIN_PANEL_ILLUSTRATIONS.support} />
              <header className="wfx-admin-panel-head"><h2>Support Overview</h2></header>
              <div className="wfx-admin-stat-row">
                <div><strong>{data.support.openTickets}</strong><span>Open Tickets</span></div>
                <div><strong>{data.support.resolvedToday}</strong><span>Resolved Today</span></div>
                <div><strong>{data.support.avgResponseMinutes}m</strong><span>Avg Response</span></div>
              </div>
            </article>
          )}

          <article className="wfx-admin-panel glass-surface has-illustration">
            <PanelIllustration context={ADMIN_PANEL_ILLUSTRATIONS.notifications} />
            <header className="wfx-admin-panel-head"><h2>Notification Center</h2></header>
            <ul className="wfx-admin-note-list">
              {data.notifications.map((note) => (
                <li key={note.id}>{note.message}</li>
              ))}
            </ul>
          </article>
        </div>
      </section>

      {data.auditLog.length > 0 && (
        <section className="wfx-admin-panel glass-surface has-illustration">
          <PanelIllustration context={ADMIN_PANEL_ILLUSTRATIONS.audit} />
          <header className="wfx-admin-panel-head">
            <h2>Audit Log</h2>
            <Link to="/admin/audit" className="text-action">View all</Link>
          </header>
          <div className="wfx-admin-table-wrap">
            <table className="wfx-admin-table">
              <thead>
                <tr><th>User</th><th>Action</th><th>Module</th><th>Time</th></tr>
              </thead>
              <tbody>
                {data.auditLog.map((row, index) => (
                  <tr key={`${row.user}-${index}`}>
                    <td>{row.user}</td>
                    <td>{row.action}</td>
                    <td>{row.module}</td>
                    <td>{row.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="wfx-admin-panel glass-surface has-illustration">
        <PanelIllustration context={ADMIN_PANEL_ILLUSTRATIONS.schedule} />
        <header className="wfx-admin-panel-head"><h2>Today&apos;s Schedule</h2></header>
        <div className="wfx-admin-calendar-grid">
          {data.calendar.map((item) => (
            <div key={item.label} className="wfx-admin-calendar-card">
              <strong>{item.count}</strong>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="wfx-admin-fab-wrap">
        {fabOpen && (
          <div className="wfx-admin-fab-menu glass-surface">
            {[
              { label: 'New Result', route: '/admin/results' },
              { label: 'New Opportunity', route: '/admin/opportunities' },
              { label: 'New Blog', route: '/admin/content/blogs' },
              { label: 'New FAQ', route: '/admin/content/faqs' },
              { label: 'Upload Media', route: '/admin/media' },
              { label: 'Create User', route: '/admin/users' },
            ].map((item) => (
              <Link key={item.label} to={item.route} className="wfx-admin-fab-item" onClick={() => setFabOpen(false)}>
                {item.label}
              </Link>
            ))}
          </div>
        )}
        <button type="button" className="wfx-admin-fab" aria-label="Quick create" onClick={() => setFabOpen((v) => !v)}>
          <Plus size={22} />
        </button>
      </div>
    </div>
  );
}

export function AdminDashboardScreen() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: fetchAdminDashboard,
  });

  if (isLoading) return <LoadingState label="Loading admin dashboard…" />;
  if (isError || !data) return <ErrorState message="Could not load admin dashboard." onRetry={() => void refetch()} />;
  return <DashboardBody data={data} />;
}
